import { useState } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import {
  Text,
  Box,
  Flex,
  Button,
  Grid,
  GridItem,
  useColorModeValue,
  Collapse,
  Icon,
} from '@chakra-ui/react';

import { DeleteIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import Divider from 'views/auto_form/components/Divider';

import { SECTIONS } from 'views/auto_form/constants';

import useIsBinderDisabled from 'views/auto_form/hooks/useIsBinderDisabled';

import {
  changeRatingInformationDrivingRecord,
  addRatingInformationDrivingRecord,
  removeRatingInformationDrivingRecord,
  changeRatingInformationDiscounts,
  addRatingInformationDiscounts,
  removeRatingInformationDiscount,
  changeRatingInformationDiscountsDiscount,
  addRatingInformationDiscountsDiscount,
  removeRatingInformationDiscountsDiscount,
} from 'reducers/autoPersonalFormReducer';

const RatingInformation = () => {
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const sectionBg = useColorModeValue('#ccc', 'gray.600');
  const sectionColor = useColorModeValue('black', 'white');
  const borderColor = useColorModeValue('#ccc', 'gray.600');

  const rating_information_driving_record = useAppSelector(
    (state) => state.autoPersonalForm.data.rating_information_driving_record,
  );

  const rating_information_discounts = useAppSelector(
    (state) => state.autoPersonalForm.data.rating_information_discounts,
  );

  const isDisabled = useIsBinderDisabled(true);
  const templateColumns = !isDisabled ? 'repeat(10, 1fr)' : 'repeat(9, 1fr)';

  const onDrivingRecordChange = (event: any, index: number) => {
    const { name, value } = event.target;

    dispatch(changeRatingInformationDrivingRecord({ name, index, value }));
  };

  const addNewDrivingRecord = () => {
    dispatch(addRatingInformationDrivingRecord());
  };

  const removeDrivingRecord = (_: any, index: number) => {
    dispatch(removeRatingInformationDrivingRecord(index));
  };

  const addNewDiscount = () => {
    dispatch(addRatingInformationDiscounts());
  };

  const removeDiscount = (_: any, index: number) => {
    dispatch(removeRatingInformationDiscount(index));
  };

  const onDiscountChange = (event: any, index: number) => {
    const { name, value } = event.target;

    dispatch(changeRatingInformationDiscounts({ name, index, value }));
  };

  const onSubDiscountChange = (
    event: any,
    index: number,
    discountIndex: number,
  ) => {
    const { name, value } = event.target;

    dispatch(
      changeRatingInformationDiscountsDiscount({
        name,
        index,
        discountIndex,
        value,
      }),
    );
  };

  const removeSubDiscount = (_: any, index: number, discountIndex: number) => {
    dispatch(
      removeRatingInformationDiscountsDiscount({ index, discountIndex }),
    );
  };

  const addNewSubDiscount = (index: number) => {
    dispatch(addRatingInformationDiscountsDiscount(index));
  };

  return (
    <Card mb="20px" id="sectionRatingInformation">
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="bold" fontSize="xl">
          Rating Information Summary{' '}
          <Text textColor="red" display="inline">
            (dev mode)
          </Text>
        </Text>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Flex>

      <Collapse in={!isCollapsed} animateOpacity>
        <Divider mt="20px">Driving Record</Divider>

        <Box ml={rating_information_driving_record.length > 1 ? '20px' : ''}>
          {rating_information_driving_record.map((driving_record, index) => {
            return (
              <Box key={index}>
                {rating_information_driving_record.length > 1 && (
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                    mt="20px"
                  >
                    Entry {index + 1}
                  </Box>
                )}

                <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                  <GridItem colSpan={2} />
                  <GridItem
                    colSpan={5}
                    textAlign="center"
                    borderBottom="1px"
                    borderColor={borderColor}
                    pb="5px"
                    mb="5px"
                  >
                    Driving Record
                  </GridItem>
                  <GridItem
                    colSpan={2}
                    textAlign="center"
                    borderBottom="1px"
                    borderColor="#ccc"
                    pb="5px"
                    mb="5px"
                  >
                    Driver No.
                  </GridItem>
                </Grid>

                <Grid templateColumns={templateColumns} gap="20px">
                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Auto(No.)"
                    name="auto_nr"
                    value={driving_record.auto_nr}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Class"
                    name="auto_class"
                    value={driving_record.auto_class}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="BI"
                    name="bi"
                    value={driving_record.bi}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="PD"
                    name="pd"
                    value={driving_record.pd}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="AB"
                    name="ab"
                    value={driving_record.ab}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="DCPD"
                    name="dcpd"
                    value={driving_record.dcpd}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Coll/AP"
                    name="coll_ap"
                    value={driving_record.coll_ap}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Princ."
                    name="driver_principal"
                    value={driving_record.driver_principal}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Sec."
                    name="driver_secondary"
                    value={driving_record.driver_secondary}
                    onChange={(e) => onDrivingRecordChange(e, index)}
                    disabled
                  />

                  {!isDisabled && (
                    <Button
                      mt="30px"
                      onClick={(e) => removeDrivingRecord(e, index)}
                    >
                      <DeleteIcon />
                    </Button>
                  )}
                </Grid>

                <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                  <GridItem colSpan={2} />
                  <GridItem
                    colSpan={3}
                    textAlign="center"
                    borderBottom="1px"
                    borderColor="#ccc"
                    pb="5px"
                    mb="5px"
                  >
                    At-Fault Claim Surcharges
                  </GridItem>
                  <GridItem
                    colSpan={4}
                    textAlign="center"
                    borderBottom="1px"
                    borderColor="#ccc"
                    pb="5px"
                    mb="5px"
                  >
                    Conviction Surcharges
                  </GridItem>
                </Grid>

                <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                  <GridItem colSpan={2} />
                  <GridItem colSpan={3}>
                    <Flex gap="20px">
                      <Box w="80%">
                        <FormInput
                          section={SECTIONS.RATING_INFORMATION}
                          label="Description"
                          name="at_fault_claim_description"
                          value={driving_record.at_fault_claim_description}
                          onChange={(e) => onDrivingRecordChange(e, index)}
                          disabled
                        />
                      </Box>

                      <Box w="20%">
                        <FormInput
                          section={SECTIONS.RATING_INFORMATION}
                          label="%"
                          name="at_fault_claim_percentage"
                          value={driving_record.at_fault_claim_percentage}
                          onChange={(e) => onDrivingRecordChange(e, index)}
                          disabled
                        />
                      </Box>
                    </Flex>
                  </GridItem>

                  <GridItem colSpan={4} gap="20px">
                    <Flex gap="20px">
                      <Box w="80%">
                        <FormInput
                          section={SECTIONS.RATING_INFORMATION}
                          label="Description"
                          name="conviction_description"
                          value={driving_record.conviction_description}
                          onChange={(e) => onDrivingRecordChange(e, index)}
                          disabled
                        />
                      </Box>

                      <Box w="20%">
                        <FormInput
                          section={SECTIONS.RATING_INFORMATION}
                          label="%"
                          name="conviction_percentage"
                          value={driving_record.conviction_percentage}
                          onChange={(e) => onDrivingRecordChange(e, index)}
                          disabled
                        />
                      </Box>
                    </Flex>
                  </GridItem>

                  <GridItem colSpan={1} />
                </Grid>
              </Box>
            );
          })}

          {!isDisabled && (
            <Box mt="20px">
              <Button variant="brand" onClick={addNewDrivingRecord}>
                Add new
              </Button>
            </Box>
          )}
        </Box>

        <Divider mt="20px">Discounts</Divider>

        <Box ml={rating_information_discounts.length > 1 ? '20px' : ''}>
          {rating_information_discounts.map((discount, index) => {
            return (
              <Box key={index}>
                {rating_information_discounts.length > 1 && (
                  <Box
                    backgroundColor={sectionBg}
                    color={sectionColor}
                    rounded="5px"
                    p="5px"
                    pl="10px"
                    mt="20px"
                  >
                    Entry {index + 1}
                  </Box>
                )}

                <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                  <GridItem colSpan={3} />
                  <GridItem
                    colSpan={4}
                    textAlign="center"
                    borderBottom="1px"
                    borderColor="#ccc"
                    pb="5px"
                    mb="5px"
                  >
                    Rate Group
                  </GridItem>
                  <GridItem colSpan={3} />
                </Grid>

                <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Auto(No.)"
                    name="auto_nr"
                    value={discount.auto_nr}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="List Price New"
                    name="list_price_new"
                    value={discount.list_price_new}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Vehicle Code"
                    name="vehicle_code"
                    value={discount.vehicle_code}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="AB"
                    name="ab"
                    value={discount.ab}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="DCPD"
                    name="dcpd"
                    value={discount.dcpd}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Coll/AP"
                    name="coll_ap"
                    value={discount.coll_ap}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Comp/SP"
                    name="comp_sp"
                    value={discount.comp_sp}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Location"
                    name="location"
                    value={discount.location}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="P.C."
                    name="postal_code"
                    value={discount.postal_code || ''}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  <FormInput
                    section={SECTIONS.RATING_INFORMATION}
                    label="Territory"
                    name="territory"
                    value={discount.territory}
                    onChange={(e) => onDiscountChange(e, index)}
                    disabled
                  />

                  {!isDisabled && (
                    <Button
                      mt="30px"
                      onClick={(e) => removeDiscount(e, index)}
                      disabled={isDisabled}
                    >
                      <DeleteIcon />
                    </Button>
                  )}
                </Grid>

                <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                  <GridItem colSpan={4} />
                  <GridItem
                    colSpan={5}
                    textAlign="center"
                    borderBottom="1px"
                    borderColor="#ccc"
                    pb="5px"
                    mb="5px"
                  >
                    Discounts
                  </GridItem>
                  <GridItem colSpan={1} />
                </Grid>

                {discount.discounts.map((subDiscount, subIndex) => {
                  return (
                    <Grid
                      templateColumns={templateColumns}
                      gap="20px"
                      mt="20px"
                      key={subIndex}
                    >
                      <GridItem colSpan={4} />
                      <GridItem colSpan={4}>
                        <FormInput
                          section={SECTIONS.RATING_INFORMATION}
                          label="Description"
                          name="discount_description"
                          value={subDiscount.discount_description}
                          onChange={(e) =>
                            onSubDiscountChange(e, index, subIndex)
                          }
                          disabled
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <FormInput
                          section={SECTIONS.RATING_INFORMATION}
                          label="%"
                          name="discount_percentage"
                          value={subDiscount.discount_percentage}
                          onChange={(e) =>
                            onSubDiscountChange(e, index, subIndex)
                          }
                          disabled
                        />
                      </GridItem>
                      {!isDisabled && (
                        <GridItem colSpan={1}>
                          {subIndex !== 0 && (
                            <Button
                              mt="30px"
                              onClick={(e) =>
                                removeSubDiscount(e, index, subIndex)
                              }
                            >
                              <DeleteIcon />
                            </Button>
                          )}
                        </GridItem>
                      )}
                    </Grid>
                  );
                })}

                {!isDisabled && (
                  <Grid templateColumns={templateColumns} gap="20px" mt="20px">
                    <GridItem colSpan={4} />
                    <GridItem colSpan={6}>
                      <Button
                        variant="brand"
                        onClick={() => addNewSubDiscount(index)}
                      >
                        Add new
                      </Button>
                    </GridItem>
                  </Grid>
                )}
              </Box>
            );
          })}

          {!isDisabled && (
            <Box mt="20px">
              <Button variant="brand" onClick={addNewDiscount}>
                Add new
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

export default RatingInformation;
